import React, { useState, useEffect, useCallback } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, ChevronLeft, ChevronRight, Moon, FileText, Loader2, Calendar, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import moment from "moment";
import "moment/locale/ca";

moment.locale("ca");

const DAYS_CA = ["Dl", "Dm", "Dc", "Dj", "Dv", "Ds", "Dg"];

function minutesToHHMM(mins) {
  if (!mins || mins <= 0) return "–";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m > 0 ? m.toString().padStart(2, "0") : "00"}`;
}

function buildSegments(dayRecords) {
  const sorted = [...dayRecords]
    .filter(r => !r.is_deleted)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const segments = [];
  let pending = null;
  for (const r of sorted) {
    if (r.type === "entry") {
      pending = moment(r.timestamp).format("HH:mm");
    } else if (r.type === "exit" && pending) {
      segments.push({ entry: pending, exit: moment(r.timestamp).format("HH:mm") });
      pending = null;
    }
  }
  if (pending) segments.push({ entry: pending, exit: null });
  return segments;
}

function calcMins(segments, date) {
  let total = 0;
  for (const s of segments) {
    if (!s.entry || !s.exit) continue;
    const entry = moment(`${date} ${s.entry}`, "YYYY-MM-DD HH:mm");
    let exit = moment(`${date} ${s.exit}`, "YYYY-MM-DD HH:mm");
    if (exit.isBefore(entry)) exit.add(1, "day");
    total += exit.diff(entry, "minutes");
  }
  return Math.max(0, total);
}

function groupByWeek(days) {
  const weeks = [];
  let cur = [], curIW = null;
  for (const d of days) {
    const iw = d.isoWeek();
    if (curIW !== null && iw !== curIW) { weeks.push(cur); cur = []; }
    cur.push(d);
    curIW = iw;
  }
  if (cur.length) weeks.push(cur);
  return weeks;
}

export default function MyHours() {
  const { currentVenue } = useVenue();
  const [user, setUser] = useState(null);
  const [records, setRecords] = useState([]);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearMonth, setYearMonth] = useState(moment().format("YYYY-MM"));
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [member, setMember] = useState(null);

  useEffect(() => { hosys.auth.me().then(u => setUser(u)); }, []);

  const load = useCallback(async () => {
    if (!currentVenue || !user) return;
    setLoading(true);
    const start = moment(yearMonth, "YYYY-MM").startOf("month").format("YYYY-MM-DD");
    const end = moment(yearMonth, "YYYY-MM").endOf("month").format("YYYY-MM-DD");

    const [recs, cts, members] = await Promise.all([
      hosys.entities.TimeRecord.filter({
        venue_id: currentVenue.id,
        user_email: user.email,
        date_from: start,
        date_to: end,
        limit: 2000,
      }, "date"),
      hosys.entities.WorkerContract.filter({ venue_id: currentVenue.id, user_email: user.email, active: true }),
      hosys.entities.VenueMember.filter({ venue_id: currentVenue.id, user_email: user.email }),
    ]);

    setRecords(recs);
    setContract(cts.length > 0 ? cts[0] : null);
    setMember(members.length > 0 ? members[0] : null);
    setLoading(false);
  }, [currentVenue, user, yearMonth]);

  useEffect(() => { load(); }, [load]);

  const start = moment(yearMonth, "YYYY-MM").startOf("month");
  const end = moment(yearMonth, "YYYY-MM").endOf("month");
  const days = [];
  for (let d = start.clone(); d.isSameOrBefore(end); d.add(1, "day")) days.push(d.clone());
  const weeks = groupByWeek(days);

  const recordsByDate = {};
  for (const r of records) {
    const d = r.date;
    if (!recordsByDate[d]) recordsByDate[d] = [];
    recordsByDate[d].push(r);
  }

  const weeklyMins = (weekDays) => weekDays.reduce((s, d) => {
    const date = d.format("YYYY-MM-DD");
    return s + calcMins(buildSegments(recordsByDate[date] || []), date);
  }, 0);

  const monthlyMins = days.reduce((s, d) => {
    const date = d.format("YYYY-MM-DD");
    return s + calcMins(buildSegments(recordsByDate[date] || []), date);
  }, 0);

  const contractWeeklyMins = contract?.weekly_hours ? Math.round(contract.weekly_hours * 60) : 0;
  const contractMonthlyMins = contractWeeklyMins ? Math.round(contractWeeklyMins * (weeks.length >= 5 ? 4.33 : weeks.length)) : 0;

  const prevMonth = () => setYearMonth(moment(yearMonth, "YYYY-MM").subtract(1, "month").format("YYYY-MM"));
  const nextMonth = () => setYearMonth(moment(yearMonth, "YYYY-MM").add(1, "month").format("YYYY-MM"));

  const downloadPdf = async () => {
    if (!member) return;
    setDownloadingPdf(true);
    try {
      const [y, m] = yearMonth.split("-");
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const url = `${apiBase}/api/attendance/jornada-pdf/${currentVenue.id}/${member.member_id || member.id}?month=${parseInt(m)}&year=${parseInt(y)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error("Error");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `jornada_${yearMonth}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { console.error(e); }
    setDownloadingPdf(false);
  };

  if (loading) return <QuickLoader />;

  const monthLabel = moment(yearMonth, "YYYY-MM").format("MMMM YYYY");

  return (
    <div className="space-y-5" data-testid="my-hours-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PageHeader title="Les meves hores" subtitle="Registre mensual de la teva jornada" />
        <div className="flex items-center gap-2">
          {member && (
            <Button
              variant="outline"
              size="sm"
              onClick={downloadPdf}
              disabled={downloadingPdf}
              className="gap-1.5 text-xs"
              data-testid="download-jornada-btn"
            >
              {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Descarregar PDF
            </Button>
          )}
        </div>
      </div>

      {/* Month nav */}
      <div className="flex items-center gap-3 justify-center">
        <Button variant="ghost" size="icon" onClick={prevMonth} className="h-8 w-8">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-base font-bold text-gray-800 capitalize min-w-[160px] text-center">{monthLabel}</span>
        <Button variant="ghost" size="icon" onClick={nextMonth} className="h-8 w-8">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm p-3 text-center">
          <Clock className="w-5 h-5 mx-auto mb-1 text-[#7B2D3F]" />
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Hores treballades</p>
          <p className="text-xl font-black text-gray-900">{minutesToHHMM(monthlyMins)}</p>
        </Card>
        {contractMonthlyMins > 0 && (
          <Card className="border-0 shadow-sm p-3 text-center">
            <TrendingUp className="w-5 h-5 mx-auto mb-1 text-blue-500" />
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Contracte mensual</p>
            <p className="text-xl font-black text-blue-600">{minutesToHHMM(contractMonthlyMins)}</p>
          </Card>
        )}
        <Card className={`border-0 shadow-sm p-3 text-center ${contractMonthlyMins > 0 ? (monthlyMins > contractMonthlyMins ? "bg-red-50" : monthlyMins < contractMonthlyMins ? "bg-amber-50" : "bg-emerald-50") : ""}`}>
          <Calendar className="w-5 h-5 mx-auto mb-1 text-gray-400" />
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Dies treballats</p>
          <p className="text-xl font-black">{Object.keys(recordsByDate).filter(d => recordsByDate[d].some(r => !r.is_deleted)).length}</p>
        </Card>
        {contractMonthlyMins > 0 && (
          <Card className={`border-0 shadow-sm p-3 text-center ${monthlyMins - contractMonthlyMins > 0 ? "bg-red-50" : monthlyMins - contractMonthlyMins < 0 ? "bg-amber-50" : "bg-emerald-50"}`}>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mt-1">Diferència</p>
            <p className={`text-xl font-black ${monthlyMins - contractMonthlyMins > 0 ? "text-red-600" : monthlyMins - contractMonthlyMins < 0 ? "text-amber-600" : "text-emerald-600"}`}>
              {monthlyMins - contractMonthlyMins > 0 ? "+" : ""}{minutesToHHMM(Math.abs(monthlyMins - contractMonthlyMins))}
            </p>
            <p className="text-[9px] text-gray-400">{monthlyMins - contractMonthlyMins > 0 ? "Excés" : monthlyMins - contractMonthlyMins < 0 ? "Per sota" : "Exacte"}</p>
          </Card>
        )}
      </div>

      {/* Weekly detail tables */}
      <div className="space-y-4">
        {weeks.map((weekDays, wi) => {
          const wMins = weeklyMins(weekDays);
          const wDiff = wMins - contractWeeklyMins;
          return (
            <Card key={`el-${wi}`} className="border-0 shadow-sm overflow-hidden" data-testid={`week-card-${wi}`}>
              {/* Week header */}
              <div className="flex items-center justify-between bg-indigo-50 px-4 py-2.5 border-b border-indigo-100">
                <div className="flex items-center gap-2">
                  <Badge className="bg-indigo-100 text-indigo-700 border-0 text-xs font-bold">
                    Setmana {wi + 1}
                  </Badge>
                  <span className="text-xs text-indigo-500">
                    {weekDays[0].format("D MMM")} – {weekDays[weekDays.length - 1].format("D MMM")}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-indigo-700">{minutesToHHMM(wMins)}</span>
                  {contractWeeklyMins > 0 && (
                    <span className={`text-xs font-semibold ${wDiff > 0 ? "text-red-500" : wDiff < 0 ? "text-amber-500" : "text-emerald-500"}`}>
                      / {minutesToHHMM(contractWeeklyMins)}
                      {wMins > 0 && (
                        <span className="ml-1">
                          ({wDiff > 0 ? "+" : ""}{minutesToHHMM(Math.abs(wDiff))})
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>

              {/* Days table */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] text-gray-400 uppercase tracking-wider">
                    <th className="text-left px-4 py-2 font-semibold">Dia</th>
                    <th className="text-center px-2 py-2 font-semibold">Entrada</th>
                    <th className="text-center px-2 py-2 font-semibold">Sortida</th>
                    <th className="text-right px-4 py-2 font-semibold">Hores</th>
                  </tr>
                </thead>
                <tbody>
                  {weekDays.map(d => {
                    const date = d.format("YYYY-MM-DD");
                    const isWeekend = d.day() === 0 || d.day() === 6;
                    const isToday = d.isSame(moment(), "day");
                    const dayRecs = recordsByDate[date] || [];
                    const segs = buildSegments(dayRecs);
                    const mins = calcMins(segs, date);
                    const dayLabel = DAYS_CA[(d.day() + 6) % 7];

                    return (
                      <tr
                        key={date}
                        className={`border-t border-gray-50 ${isToday ? "bg-[#7B2D3F]/5" : isWeekend ? "bg-gray-50/50" : ""}`}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold w-5 ${isWeekend ? "text-gray-300" : "text-gray-400"}`}>{dayLabel}</span>
                            <span className={`font-semibold ${isToday ? "text-[#7B2D3F]" : isWeekend ? "text-gray-300" : "text-gray-700"}`}>
                              {d.format("D")}
                            </span>
                            {isToday && <Badge className="bg-[#7B2D3F] text-white border-0 text-[9px] px-1.5 py-0">avui</Badge>}
                          </div>
                        </td>
                        <td className="text-center px-2 py-2.5">
                          {segs.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {segs.map((s, i) => (
                                <span key={`entry-${s.entry}-${i}`} className="text-xs font-medium text-emerald-600">{s.entry}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">–</span>
                          )}
                        </td>
                        <td className="text-center px-2 py-2.5">
                          {segs.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {segs.map((s, i) => {
                                const night = s.exit && s.entry && s.exit < s.entry;
                                return (
                                  <span key={`exit-${s.exit || 'open'}-${i}`} className="text-xs font-medium text-red-500 inline-flex items-center justify-center gap-0.5">
                                    {s.exit || "..."}
                                    {night && <Moon className="w-3 h-3 text-indigo-400" />}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">–</span>
                          )}
                        </td>
                        <td className="text-right px-4 py-2.5">
                          {mins > 0 ? (
                            <span className="text-xs font-bold text-gray-800">{minutesToHHMM(mins)}</span>
                          ) : segs.some(s => !s.exit) ? (
                            <span className="text-xs text-amber-500 font-medium">En curs...</span>
                          ) : (
                            <span className="text-xs text-gray-300">–</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          );
        })}
      </div>

      {/* Monthly summary footer */}
      <Card className="border-0 shadow-sm bg-[#7B2D3F]/5 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Total mensual</p>
            <p className="text-2xl font-black text-[#7B2D3F]">{minutesToHHMM(monthlyMins)}</p>
          </div>
          {contractMonthlyMins > 0 && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Contracte: {minutesToHHMM(contractMonthlyMins)}</p>
              <p className={`text-lg font-bold ${monthlyMins - contractMonthlyMins > 0 ? "text-red-600" : monthlyMins - contractMonthlyMins < 0 ? "text-amber-600" : "text-emerald-600"}`}>
                {monthlyMins - contractMonthlyMins > 0 ? "+" : ""}{minutesToHHMM(Math.abs(monthlyMins - contractMonthlyMins))}
                <span className="text-xs font-normal text-gray-400 ml-1">
                  {monthlyMins - contractMonthlyMins > 0 ? "excés" : monthlyMins - contractMonthlyMins < 0 ? "per sota" : "exacte"}
                </span>
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
